import { inject, Injectable } from '@angular/core';
import { NewsfeedApiService } from '@epicuro-next/data/engagement/engagement-api';
import {
  NewsfeedItemResponseDto,
  NewsfeedResponseDto,
  NewsfeedType,
} from '@epicuro-next/data/engagement/engagement-models';
import {
  isCommercialList,
  isPowerList,
} from '@epicuro-next/features/engagement/list-utils';
import { BasketItemSource } from '@epicuro-next/models/basket';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import { LocalStorageService } from '@epicuro-next/platform/storage';
import { setProductSource } from '@epicuro-next/state/config-strip-state';
import { trackList } from '@epicuro-next/state/product-list-state';
import { go } from '@epicuro-next/state/router-store';
import { isNonNullish } from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import {
  catchError,
  debounceTime,
  EMPTY,
  filter,
  Observable,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { map, tap } from 'rxjs/operators';

const PAGE_SIZE = 1;
const PAGE = 1;

type State = {
  availableFeeds: NewsfeedResponseDto[];
  showedFeedIds: string[];
  showModal: boolean;
};

const initial: State = {
  availableFeeds: [],
  showedFeedIds: [],
  showModal: false,
};

const SHOWN_FEED_IDS_STORAGE_KEY = 'welcome_feed_ids_shown';

@Injectable()
export class WelcomePanelComponentStore extends ComponentStore<State> {
  private readonly store = inject(Store);
  private readonly feedService = inject(NewsfeedApiService);

  private readonly fetchParams = {
    page: PAGE,
    pageSize: PAGE_SIZE,
    type: NewsfeedType.WelcomePromotion,
  };
  private readonly availableFeeds$ = this.select(
    ({ availableFeeds }) => availableFeeds,
  );
  private readonly showedFeedIds$ = this.select(
    ({ showedFeedIds }) => showedFeedIds,
  );
  public readonly feedToShow$ = this.availableFeeds$.pipe(
    withLatestFrom(this.showedFeedIds$),
    map(([availableFeeds, showedFeedIds]) => {
      if (availableFeeds.length === 0) {
        return null;
      }

      // check and show only the latest feed
      const latestFeed = availableFeeds[availableFeeds.length - 1];

      if (showedFeedIds.includes(latestFeed.id)) {
        return null;
      }

      return latestFeed;
    }),
  );

  public readonly showModal$ = this.select(({ showModal }) => showModal);

  constructor(private localStorage: LocalStorageService) {
    super({
      ...initial,
      showedFeedIds: localStorage.get(SHOWN_FEED_IDS_STORAGE_KEY) ?? [],
    });
  }

  public listenUrlClick = this.effect(
    (feedReference$: Observable<NewsfeedItemResponseDto>) => {
      return feedReference$.pipe(
        tap((feedRef: NewsfeedItemResponseDto) => {
          this.store.dispatch(
            setProductSource({
              source: BasketItemSource.LIST_MODAL,
              details: {
                listId: [feedRef.id],
                isPowerList: isPowerList(feedRef),
                isCommercial: isCommercialList(feedRef),
                listType: feedRef.type,
              },
            }),
          );
          this.store.dispatch(
            trackList({
              payload: {
                source: 'welcome-modal',
                productListId: feedRef.id,
              },
            }),
          );
          this.toggleModal(false);
          this.store.dispatch(
            go({
              commands: [APP_PRIVATE_PATHS.productList.path, feedRef.id],
            }),
          );
        }),
      );
    },
  );

  public getFeeds = this.effect<void>((trigger$) => {
    return trigger$.pipe(
      tap(() => {
        this.patchState({
          showModal: false,
          availableFeeds: [],
        });
      }),
      switchMap(() => {
        return this.feedService.getNewsfeed(this.fetchParams).pipe(
          tap((response) => {
            this.patchState({
              availableFeeds: response.items,
            });
          }),
          catchError(() => {
            console.warn('Failed to get welcome modal feeds');
            return EMPTY;
          }),
        );
      }),
    );
  });

  public openFeedInModal = this.effect<NewsfeedResponseDto | null>((feed$) => {
    return feed$.pipe(
      filter(isNonNullish),
      take(1),
      // wait a little until home content is loaded and rendered
      debounceTime(2000),
      tap((feed: NewsfeedResponseDto) => {
        this.toggleModal(true);

        this.markFeedAsShown(feed.id);
      }),
    );
  })(this.feedToShow$);

  private markFeedAsShown(feedId: string) {
    this.localStorage.set(SHOWN_FEED_IDS_STORAGE_KEY, [feedId]);
  }

  public toggleModal(showModal: boolean) {
    this.patchState({
      showModal,
    });
  }
}
